import { useQuery } from "@tanstack/react-query";
import { ApiError } from "@evocha/api-client";
import { api } from "./api";

export const queryKeys = {
  wallet: ["wallet"] as const,
  orders: (params: Record<string, unknown>) => ["orders", params] as const,
  summary: (period: string) => ["summary", period] as const,
  daily: (days: number) => ["daily-sales", days] as const,
  statement: (params: Record<string, unknown>) => ["statement", params] as const,
  fundingOrders: ["funding-orders"] as const,
};

export function useWallet() {
  // Live float: the topbar chip, dashboard and sell screen all read this cache,
  // so one 15 s poll keeps every surface in step without extra requests.
  return useQuery({
    queryKey: queryKeys.wallet,
    queryFn: () => api.wallet(),
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  });
}

export function useSummary(period: "daily" | "weekly" | "monthly") {
  return useQuery({
    queryKey: queryKeys.summary(period),
    queryFn: () => api.summary(period),
  });
}

export function useDailySales(days: number) {
  return useQuery({
    queryKey: queryKeys.daily(days),
    queryFn: () => api.dailySales(days),
  });
}

export function useOrders(
  params: {
    status?: string;
    mno?: string;
    q?: string;
    from?: string;
    to?: string;
    cursor?: string;
    limit?: number;
  },
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.orders(params),
    queryFn: () => api.orders(params),
    enabled,
  });
}

export function useStatement(
  params: { from?: string; to?: string; cursor?: string; limit?: number },
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.statement(params),
    queryFn: () => api.statement(params),
    enabled,
  });
}

export function useFundingOrders(limit = 20) {
  return useQuery({
    queryKey: queryKeys.fundingOrders,
    queryFn: () => api.fundingOrders(limit),
  });
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.problem.detail ?? error.message;
  }
  if (error instanceof Error) return error.message;
  return "Something went wrong. Try again.";
}

export function relativeTime(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  const diff = Date.now() - date.getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

export function shortDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}
